import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import { deepCopy } from '../../../util/src/lib/deep-copy';
import { Configuration } from './../../config/configuration';
import { MenuService } from './menu.service';

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  /**
   * @description 保存麵包屑最後出現的資料 for 語系切換時使用
   */
  private breadcrumbs: MenuItem[] = [];
  private breadcrumbs$ = new BehaviorSubject<MenuItem[]>([]);
  private currentCompanyId = '';

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    private configuration: Configuration,
    private menuService: MenuService
  ) {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route: ActivatedRoute) => {
          while (route.firstChild) {
            route = route.firstChild;
          }

          this.currentCompanyId = route.snapshot.params.company || '';

          return route;
        }),
        filter((route: ActivatedRoute) => route.outlet === 'primary'),
        mergeMap((route: ActivatedRoute) => route.data)
      )
      .subscribe((data) => {
        if (data.breadcrumbs && data.breadcrumbs.length > 0) {
          const prefixItems: MenuItem[] = [
            {
              label: 'SigvLibrary.Home',
              routerLink: this.configuration.routePath.index,
            },
          ];
          if (this.currentCompanyId) {
            const menuSubscribeDone$ = new Subject();

            this.menuService
              .getCompanysFromMenuTreesObservable()
              .pipe(takeUntil(menuSubscribeDone$))
              .subscribe((companys) => {
                if (companys.length) {
                  menuSubscribeDone$.next();
                  menuSubscribeDone$.complete();
                }

                const mappingCompany = companys.find(
                  (company) => company.companyId === this.currentCompanyId
                );

                if (mappingCompany) {
                  prefixItems.push({
                    label:
                      mappingCompany.companyShortName ?? this.currentCompanyId,
                  });
                }
                this.breadcrumbs = [...prefixItems, ...data.breadcrumbs];
                this.breadcrumbs$.next(
                  this.cloneAndTranslateLable(this.breadcrumbs)
                );
              });
          } else {
            this.breadcrumbs = [...prefixItems, ...data.breadcrumbs];
            this.breadcrumbs$.next(
              this.cloneAndTranslateLable(this.breadcrumbs)
            );
          }
        } else {
          this.breadcrumbs = [];
          this.breadcrumbs$.next(this.breadcrumbs);
        }
      });

    this.translateService.onLangChange.subscribe(() => {
      this.breadcrumbs$.next(this.cloneAndTranslateLable(this.breadcrumbs));
    });
  }

  private cloneAndTranslateLable(menuItems: MenuItem[]): MenuItem[] {
    const clone = deepCopy(menuItems) as MenuItem[];
    const queue = [clone];

    while (queue.length > 0) {
      const menus = queue.shift();
      menus?.forEach((m) => {
        m.label =
          m.label == null || m.label === ''
            ? ' '
            : this.translateService.instant(m.label);

        if (m.items && m.items.length > 0) {
          queue.push(m.items);
        }
      });
    }

    return clone;
  }

  getBreadcrumbsObservable(): Observable<MenuItem[]> {
    return this.breadcrumbs$.asObservable();
  }

  hideBreadcroumbs(): void {
    this.breadcrumbs$.next([]);
  }
}
